import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  CheckCircle,
  TrendingUp,
  Clock,
  Shield,
  Zap,
  Users,
  FileText,
  BarChart3,
  ArrowRight,
  LogOut,
} from 'lucide-react';

const Landing = () => {
  const { user, logout, isOperator, isGlobalAdmin, isClient } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleGetStarted = () => {
    if (user) {
      if (isGlobalAdmin()) {
        navigate('/global-admin/dashboard');
      } else if (isOperator()) {
        navigate('/operator/dashboard');
      } else {
        navigate('/client/dashboard');
      }
    } else {
      navigate('/login');
    }
  };

  const features = [
    {
      icon: Zap,
      title: 'Automatización Inteligente',
      description: 'Tareas recurrentes se generan automáticamente cada mes. Cero gestión manual.',
      color: 'text-amber-500',
    },
    {
      icon: Clock,
      title: 'SLA Garantizado',
      description: 'Compromisos claros: 5 dias laborables o 48 horas laborables segun pack.',
      color: 'text-blue-500',
    },
    {
      icon: Shield,
      title: 'Multi-tenant Seguro',
      description: 'Aislamiento total de datos por empresa. Cumplimiento RGPD garantizado.',
      color: 'text-emerald-500',
    },
    {
      icon: Users,
      title: 'Gestión de Roles',
      description: 'Control granular de accesos: Owner, Staff, ClientAdmin, ClientUser, Advisor.',
      color: 'text-purple-500',
    },
    {
      icon: FileText,
      title: 'Documentación Centralizada',
      description: 'Versionado automático, filtros avanzados, y almacenamiento seguro ilimitado.',
      color: 'text-indigo-500',
    },
    {
      icon: BarChart3,
      title: 'Auditoría Completa',
      description: 'Trazabilidad total de cada acción. Reportes de cumplimiento con un click.',
      color: 'text-secondary',
    },
  ];

  const differentiators = [
    '✨ Interfaz vibrante y moderna, nada de grises aburridos',
    '⚡ Kanban visual para gestión ágil de tareas',
    '🔔 Notificaciones proactivas para tareas bloqueadas',
    '💳 Facturación integrada con Stripe - sin complicaciones',
    '🌐 Soporte bilingüe español/inglés desde el día 1',
    '📊 Panel en tiempo real con metricas clave',
  ];

  const plans = [
    {
      id: 'pkg-gastos-bancos',
      name: 'Operacion',
      price: '129€',
      description: 'Orden operativo mensual',
      features: [
        'Facturacion y gastos en flujo estructurado',
        'Conciliacion bancaria basica',
        'Control de cobros y pagos',
        'Cierre mensual administrativo',
        'Hasta 50 documentos/mes',
        'SLA 5 dias laborables',
      ],
      popular: true,
    },
    {
      id: 'pkg-administracion-completa',
      name: 'Gestion Integral',
      price: '199€',
      description: 'Delegacion administrativa total',
      features: [
        'Incluye todo Operacion',
        'Seguimiento de impagos',
        'Informes mensuales (balance/libro diario)',
        'Paquete y comunicacion con gestoria',
        'Hasta 100 documentos/mes',
        'SLA 48 horas laborables',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-card/80 backdrop-blur-md border-b border-border shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg shadow-primary/20">
              <span className="text-primary-foreground font-bold text-xl font-heading">Z</span>
            </div>
            <h1 className="text-2xl font-heading font-bold text-primary">Zentro</h1>
          </div>

          <div className="flex items-center gap-4">
            {user ? (
              <>
                <div className="hidden sm:flex items-center gap-3 px-4 py-2 bg-primary/10 rounded-full">
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                    <span className="text-primary-foreground font-semibold text-sm">
                      {user.full_name?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <span className="font-medium text-sm">Bienvenido, {user.full_name?.split(' ')[0]}</span>
                </div>
                <Button
                  variant="outline"
                  onClick={handleGetStarted}
                  className="gap-2"
                  data-testid="go-to-dashboard-btn"
                >
                  Ir al Panel
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="gap-2"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  Desconectar
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" data-testid="login-link">
                    Iniciar Sesión
                  </Button>
                </Link>
                <Link to="/register">
                  <Button
                    className="shadow-md hover:shadow-lg shadow-primary/20 transition-all hover:scale-105 active:scale-95 duration-200"
                    data-testid="register-link"
                  >
                    Comenzar Gratis
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 text-center" data-testid="hero-section">
        <div className="max-w-4xl mx-auto">
          <Badge className="mb-6 px-4 py-2 text-sm shadow-md" variant="secondary">
            🚀 La nueva forma de gestionar tu administración
          </Badge>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-bold mb-6 leading-tight">
            Servicios Administrativos{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
              Paquetizados
            </span>
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Conecta con tu operador administrativo de forma transparente, eficiente y sin
            sorpresas. Todo tu back-office en una plataforma moderna.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={handleGetStarted}
              className="gap-2 text-lg px-8 py-6 shadow-lg hover:shadow-xl shadow-primary/30 transition-all hover:scale-105 active:scale-95 duration-200"
              data-testid="hero-cta-btn"
            >
              {user ? 'Ir a Mi Panel' : 'Comenzar Ahora'}
              <ArrowRight className="w-5 h-5" />
            </Button>
            <Link to="#features">
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 hover:bg-primary/5"
              >
                Ver Características
              </Button>
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 mt-16 max-w-3xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-heading font-bold text-primary mb-2">99.9%</div>
              <div className="text-sm text-muted-foreground">Uptime</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-heading font-bold text-primary mb-2">&lt;2h</div>
              <div className="text-sm text-muted-foreground">Tiempo de Respuesta</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-heading font-bold text-primary mb-2">100%</div>
              <div className="text-sm text-muted-foreground">Cumplimiento SLA</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="container mx-auto px-4 py-20" data-testid="features-section">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-heading font-bold mb-4">
            ¿Por qué elegir Zentro?
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Tecnología de vanguardia combinada con simplicidad extrema
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {features.map((feature, idx) => {
            const Icon = feature.icon;
            return (
              <Card
                key={idx}
                className="hover:border-primary/50 hover:shadow-lg transition-all duration-300 group"
                data-testid={`feature-card-${idx}`}
              >
                <CardHeader>
                  <div
                    className={`w-12 h-12 rounded-lg bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <Icon className={`w-6 h-6 ${feature.color}`} />
                  </div>
                  <CardTitle className="text-xl font-heading">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Differentiators */}
        <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
          <CardHeader>
            <CardTitle className="text-3xl font-heading text-center">
              🎯 Lo que nos hace diferentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {differentiators.map((diff, idx) => (
                <div key={idx} className="flex items-start gap-3" data-testid={`diff-${idx}`}>
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-1" />
                  <span className="text-foreground">{diff}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Ideal Client Section */}
      <section className="container mx-auto px-4 py-20" data-testid="ideal-client-section">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-heading font-bold mb-4">
            ¿Eres nuestro cliente ideal?
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Zentro está diseñado específicamente para empresas que buscan profesionalizar su gestión administrativa
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <Card className="hover:border-primary/50 hover:shadow-lg transition-all duration-300 group">
            <CardHeader>
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <Users className="w-6 h-6 text-emerald-500" />
              </div>
              <CardTitle className="text-xl font-heading">Startups y Pymes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Empresas de 1-50 empleados que necesitan estructurar su back-office sin contratar personal interno.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Facturación mensual &lt; 100K€</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Buscan profesionalización</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Quieren foco en su core business</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="hover:border-primary/50 hover:shadow-lg transition-all duration-300 group">
            <CardHeader>
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-600/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <TrendingUp className="w-6 h-6 text-blue-500" />
              </div>
              <CardTitle className="text-xl font-heading">Empresas en Crecimiento</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Negocios que han superado la fase inicial y necesitan sistemas escalables y procesos definidos.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Múltiples fuentes de ingresos</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Necesitan reportes regulares</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Buscan optimización fiscal</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="hover:border-primary/50 hover:shadow-lg transition-all duration-300 group">
            <CardHeader>
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500/10 to-purple-600/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <Shield className="w-6 h-6 text-purple-500" />
              </div>
              <CardTitle className="text-xl font-heading">Empresas Digitales</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Negocios nativos digitales que valoran la tecnología, transparencia y eficiencia operativa.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Operaciones 100% digitales</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Valoran la automatización</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Necesitan integración con APIs</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        <div className="mt-16 text-center">
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 max-w-4xl mx-auto">
            <CardContent className="py-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <span className="text-2xl">💡</span>
                </div>
                <h3 className="text-2xl font-heading font-bold text-amber-800">
                  ¿No estás seguro si Zentro es para ti?
                </h3>
              </div>
              <p className="text-amber-700 mb-6 max-w-2xl mx-auto">
                Agenda una llamada gratuita de 15 minutos con nuestro equipo. Te ayudaremos a evaluar si nuestra plataforma se adapta a tus necesidades específicas.
              </p>
              <Button 
                className="bg-amber-600 hover:bg-amber-700 text-white shadow-md hover:shadow-lg transition-all"
                onClick={() => window.open('https://calendly.com/douglas-quegenial/30min', '_blank')}
              >
                Agendar Consulta Gratuita
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="container mx-auto px-4 py-20 bg-muted/30 rounded-3xl" data-testid="pricing-section">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-heading font-bold mb-4">
            Planes transparentes
          </h2>
          <p className="text-xl text-muted-foreground">Sin sorpresas, sin letra pequeña</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan, idx) => (
            <Card
              key={idx}
              className={`relative hover:shadow-xl transition-all duration-300 ${
                plan.popular ? 'border-primary shadow-lg scale-105' : ''
              }`}
              data-testid={`plan-card-${idx}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-gradient-to-r from-primary to-secondary text-primary-foreground shadow-md px-4 py-1">
                    ⭐ Más Popular
                  </Badge>
                </div>
              )}
              <CardHeader className="text-center pb-8 pt-8">
                <CardTitle className="text-2xl font-heading mb-2">{plan.name}</CardTitle>
                <p className="text-muted-foreground text-sm mb-4">{plan.description}</p>
                <div className="mb-4">
                  <span className="text-5xl font-heading font-bold">{plan.price}</span>
                  <span className="text-muted-foreground">/mes</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full shadow-md hover:shadow-lg transition-all hover:scale-105 active:scale-95 duration-200"
                  variant={plan.popular ? 'default' : 'outline'}
                  onClick={() => navigate(user ? '/client/billing' : '/register')}
                >
                  {user ? 'Seleccionar Plan' : 'Comenzar'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-20" data-testid="cta-section">
        <Card className="bg-gradient-to-br from-primary via-primary to-secondary text-primary-foreground border-0 shadow-2xl">
          <CardContent className="py-16 text-center">
            <TrendingUp className="w-16 h-16 mx-auto mb-6 opacity-90" />
            <h2 className="text-4xl sm:text-5xl font-heading font-bold mb-6">
              ¿Listo para simplificar tu gestión administrativa?
            </h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Únete a empresas que ya confían en Zentro para su back-office
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={handleGetStarted}
                className="bg-background text-foreground hover:bg-background/90 gap-2 text-lg px-8 py-6 shadow-xl"
                data-testid="cta-btn"
              >
                {user ? 'Ir a Mi Panel' : 'Crear Cuenta Gratis'}
                <ArrowRight className="w-5 h-5" />
              </Button>
              {!user && (
                <Link to="/login">
                  <Button
                    size="lg"
                    variant="outline"
                    className="text-lg px-8 py-6 border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10"
                  >
                    Ya tengo cuenta
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <footer className="border-t border-border mt-20">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                  <span className="text-primary-foreground font-bold font-heading">Z</span>
                </div>
                <span className="text-xl font-heading font-bold">Zentro</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Plataforma líder en gestión administrativa paquetizada
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-3">Producto</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <a href="#features" className="hover:text-primary transition-colors">
                    Características
                  </a>
                </li>
                <li>
                  <a href="#pricing" className="hover:text-primary transition-colors">
                    Precios
                  </a>
                </li>
                <li>
                  <a href="/docs" className="hover:text-primary transition-colors">
                    Documentación
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-3">Empresa</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <a href="/about" className="hover:text-primary transition-colors">
                    Sobre Nosotros
                  </a>
                </li>
                <li>
                  <a href="/contact" className="hover:text-primary transition-colors">
                    Contacto
                  </a>
                </li>
                <li>
                  <a href="/careers" className="hover:text-primary transition-colors">
                    Trabaja con Nosotros
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-3">Legal</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <a href="/privacy" className="hover:text-primary transition-colors">
                    Privacidad
                  </a>
                </li>
                <li>
                  <a href="/terms" className="hover:text-primary transition-colors">
                    Términos
                  </a>
                </li>
                <li>
                  <a href="/cookies" className="hover:text-primary transition-colors">
                    Cookies
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border pt-8 text-center text-sm text-muted-foreground">
            <p>© 2024 Zentro. Todos los derechos reservados. Hecho con ❤️ en España</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;

